import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Layout } from './components/Layout';
import { LoginPage } from './pages/LoginPage';
import Dashboard from './pages/Dashboard';
import { Insights } from './pages/Insights';
import { Settings } from './pages/Settings';
import { ToastProvider } from './components/Toast';
import { Ethan } from './components/Ethan';
import { useScrollToTop } from './hooks/useScrollToTop';
import { PrivateRoute } from './components/PrivateRoute';
import LandingPage from './pages/LandingPage';
import Documents from './pages/Documents';
import { UserGenBlox } from './pages/UserGenBlox';
import DeploymentManager from './pages/DeploymentManager';
import { LessonPlanCreator } from './pages/LessonPlanCreator';
import { CourseOutlineCreator } from './pages/CourseOutlineCreator';
import { SocialMediaManager } from './pages/SocialMediaManager';
import { BrandingAgent } from './components/BrandingAgent';
import { PowerPointGenerator } from './pages/PowerPointGenerator';
import { PlayFormatter } from './pages/PlayFormatter';
import { StoryWeaver } from './pages/StoryWeaver';
import { SQLWriter } from './pages/SQLWriter';
import { PySparkWriter } from './pages/PySparkWriter';
import { AutoRag } from './pages/AutoRag';
import { GrantWriter } from './pages/GrantWriter';
import { GrantFinder } from './pages/GrantFinder';
import { DataDictionaryGenerator } from './pages/DataDictionaryGenerator';
import { TrainingManualCreator } from './pages/TrainingManualCreator';
import { ResearchAssistant } from './pages/ResearchAssistant';
import { AIToybox } from './pages/AIToybox';
import { LifeCoach } from './pages/LifeCoach';
import { ResumeBuilder } from './pages/ResumeBuilder';
import { WebCrafter } from './pages/WebCrafter';
import { TestScriptGenerator } from './pages/TestScriptGenerator';
import { Sidebar } from './components/Sidebar';
import { Subscriptions } from './pages/Subscriptions';
import { VisualPlayground } from './pages/VisualPlayground';
import PrincipalAI from './pages/PrincipalAI';

// ScrollToTop component definition
function ScrollToTop() {
  useScrollToTop();
  return null;
}

// Routes component to contain all the routes
function AppRoutes() {
  const { currentUser, loading } = useAuth();
  const location = useLocation();
  const isAuthenticated = !!currentUser;

  console.log('Auth State:', { isAuthenticated, loading, currentUser, pathname: location.pathname });

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="App min-h-screen bg-gray-50 flex flex-col">
      {/* Main Layout */}
      <div className="flex flex-1 relative">
        {/* Show Sidebar on all authenticated pages except landing and principal-ai */}
        {isAuthenticated && location.pathname !== '/' && !location.pathname.includes('/principal-ai') && <Sidebar />}

        {/* Main Content Area */}
        <main className="flex-1">
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<LandingPage />} />
            <Route 
              path="/login" 
              element={
                isAuthenticated ? <Navigate to="/dashboard" replace /> : <LoginPage />
              } 
            />

            {/* Principal AI - Special Route without Layout */}
            <Route 
              path="/principal-ai" 
              element={
                <PrivateRoute>
                  <PrincipalAI />
                </PrivateRoute>
              } 
            />

            {/* Private routes - wrapped with Layout */}
            <Route path="/dashboard" element={<PrivateRoute><Layout><Dashboard /></Layout></PrivateRoute>} />
            <Route path="/insights" element={<PrivateRoute><Layout><Insights /></Layout></PrivateRoute>} />
            <Route path="/settings" element={<PrivateRoute><Layout><Settings /></Layout></PrivateRoute>} />
            <Route path="/documents" element={<PrivateRoute><Layout><Documents /></Layout></PrivateRoute>} />
            <Route path="/deployment-manager" element={<PrivateRoute><Layout><DeploymentManager /></Layout></PrivateRoute>} />
            <Route path="/subscriptions" element={<PrivateRoute><Layout><Subscriptions /></Layout></PrivateRoute>} />

            {/* App-specific routes - wrapped with Layout */}
            <Route path="/web-crafter" element={<PrivateRoute><Layout><WebCrafter /></Layout></PrivateRoute>} />
            <Route path="/lesson-plan-creator" element={<PrivateRoute><Layout><LessonPlanCreator /></Layout></PrivateRoute>} />
            <Route path="/course-outline-creator" element={<PrivateRoute><Layout><CourseOutlineCreator /></Layout></PrivateRoute>} />
            <Route path="/social-media-manager" element={<PrivateRoute><Layout><SocialMediaManager /></Layout></PrivateRoute>} />
            <Route path="/branding-agent" element={<PrivateRoute><Layout><BrandingAgent /></Layout></PrivateRoute>} />
            <Route path="/powerpoint-generator" element={<PrivateRoute><Layout><PowerPointGenerator /></Layout></PrivateRoute>} />
            <Route path="/play-formatter" element={<PrivateRoute><Layout><PlayFormatter /></Layout></PrivateRoute>} />
            <Route path="/creative-writer" element={<PrivateRoute><Layout><StoryWeaver /></Layout></PrivateRoute>} />
            <Route path="/sql-writer" element={<PrivateRoute><Layout><SQLWriter /></Layout></PrivateRoute>} />
            <Route path="/pyspark-writer" element={<PrivateRoute><Layout><PySparkWriter /></Layout></PrivateRoute>} />
            <Route path="/auto-rag" element={<PrivateRoute><Layout><AutoRag /></Layout></PrivateRoute>} />
            <Route path="/grant-writer" element={<PrivateRoute><Layout><GrantWriter /></Layout></PrivateRoute>} />
            <Route path="/grant-finder" element={<PrivateRoute><Layout><GrantFinder /></Layout></PrivateRoute>} />
            <Route path="/data-dictionary-generator" element={<PrivateRoute><Layout><DataDictionaryGenerator /></Layout></PrivateRoute>} />
            <Route path="/training-manual-creator" element={<PrivateRoute><Layout><TrainingManualCreator /></Layout></PrivateRoute>} />
            <Route path="/research-assistant" element={<PrivateRoute><Layout><ResearchAssistant /></Layout></PrivateRoute>} />
            <Route path="/ai-toybox" element={<PrivateRoute><Layout><AIToybox /></Layout></PrivateRoute>} />
            <Route path="/life-coach" element={<PrivateRoute><Layout><LifeCoach /></Layout></PrivateRoute>} />
            <Route path="/resume-builder" element={<PrivateRoute><Layout><ResumeBuilder /></Layout></PrivateRoute>} />
            <Route path="/test-script-generator" element={<PrivateRoute><Layout><TestScriptGenerator /></Layout></PrivateRoute>} />
            <Route path="/visual-playground" element={<PrivateRoute><Layout><VisualPlayground /></Layout></PrivateRoute>} />

            {/* Legacy chat route for backward compatibility */}
            <Route path="/chat/:botType" element={<PrivateRoute><Layout><UserGenBlox /></Layout></PrivateRoute>} />
            
            {/* Catch all route */}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </main>
      </div>

      {/* Ethan AI Assistant */}
      {isAuthenticated && !location.pathname.includes('/visual-playground') && <Ethan />}
    </div>
  );
}

// Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('App Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center p-8">
            <h1 className="text-2xl font-bold text-red-600 mb-4">Something went wrong</h1>
            <p className="text-gray-600 mb-4">{this.state.error?.message}</p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Main App component
function App() {
  return (
    <ErrorBoundary>
      <Router>
        <AuthProvider>
          <ToastProvider>
            <ScrollToTop />
            <AppRoutes />
          </ToastProvider>
        </AuthProvider>
      </Router>
    </ErrorBoundary>
  );
}

export default App; 